import React, { useEffect, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import {
	Box,
	Button,
	Divider,
	Flex,
	FormControl,
	FormLabel,
	Heading,
	HStack,
	Link,
	Input,
	Spacer,
	Stack,
	Textarea,
	Tooltip,
} from '@chakra-ui/react';
import { EmailIcon } from '@chakra-ui/icons';

import { ServiceSelect } from '../ServiceSelect';
import { StageSelect } from '../StageSelect';
import { StatusSelect } from '../StatusSelect';
import { SecondarySelect } from '../SecondarySelect';
import { Ticket } from './ticket';
import { NewTicket } from './newTicket';
import { options, twoStageServices } from './data';

export const Management = ({ admin, id, user, wake, setWake, onUserClose }) => {
	const [loading, setLoading] = useState(true);
	const [comms, setComms] = useState([]);
	const [tickets, setTickets] = useState([]);
	const [workers, setWorkers] = useState([]);
	const [newComm, setNewComm] = useState('');
	const disabled = +admin > 2;

	const {
		critical,
		secondary_status,
		service,
		stage,
		status,
		storage_link,
		term,
	} = user;
	const balancedCritical = (critical || '').replace(/\s*$/, '');

	const [newCritical, setNewCritical] = useState(balancedCritical);
	const [newService, setNewService] = useState(service);
	const [newStage, setNewStage] = useState(String(stage));
	const [newStatus, setNewStatus] = useState(status);
	const [newSecondary, setNewSecondary] = useState(secondary_status);
	const [newStorageLink, setNewStorageLink] = useState(storage_link);
	const [newTerm, setNewTerm] = useState(term);

	const [subject, setSubject] = useState('');

	useEffect(() => {
		let active = true;
		async function load() {
			const res = await axios.get(`/management/${id}`, options());
			if (!active) {
				return;
			}
			setComms(res.data.communications);
			setTickets(res.data.tickets);
			setWorkers(res.data.workers);
			setLoading(false);
		}
		load();

		return () => {
			active = false;
		};
	}, [id, wake]);

	const saveText = (() => {
		let changes = [];
		if (newCritical !== balancedCritical) changes = [...changes, 'critical'];
		if (newStatus !== status) changes = [...changes, 'status'];
		if (newStage !== String(stage)) changes = [...changes, 'stage'];
		if (newSecondary !== secondary_status) changes = [...changes, 'secondary'];
		if (newService !== service) changes = [...changes, 'service'];
		if (newStorageLink !== storage_link) changes = [...changes, 'link'];
		if (newTerm !== term) changes = [...changes, 'term'];

		if (changes.length < 1) return 'No changes';
		if (changes.length === 5) return 'Update everything';

		return `Update ${changes.join(', ')}`;
	})();

	const showStage = twoStageServices.includes(newService);

	const handleSave = () => {
		const data = {
			newCritical,
			newStatus,
			newStage,
			newSecondary,
			newService,
			newStorageLink,
			newTerm,
		};
		if (newCritical === balancedCritical) delete data.newCritical;
		if (newStatus === status) delete data.newStatus;
		if (newStage === String(stage)) delete data.newStage;
		if (newSecondary === secondary_status) delete data.newSecondary;
		if (newService === service) delete data.newService;
		if (newStorageLink === storage_link) delete data.newStorageLink;
		if (newTerm === term) delete data.newTerm;
		axios({
			method: 'put',
			url: `/manage`,
			data: {
				...data,
				admin,
				customer: id,
			},
			...options(),
		})
			.then(() => {
				if (onUserClose) return onUserClose()
				setWake(new Date());
				// secondary_status = newSecondary;
				// service = newService;
				// stage = newStage;
				// status = newStatus;
				// term = newTerm;
			})
			.catch(console.log);
	};

	const handleCommChange = ({ target: { value } }) => setNewComm(value);
	const handleSend = () =>
		axios({
			method: 'post',
			url: `/communications`,
			data: {
				sender: admin,
				customer: id,
				content: newComm,
			},
			...options(),
		})
			.then(() => {
				if (onUserClose) return onUserClose()
				setComms([
					...comms,
					{
						sender_id: admin,
						customer_id: id,
						content: newComm,
						sent_at: new Date().toString(),
					},
				]);
				setNewComm('');
			})
			.catch(console.log);

	const handleEmail = () => {
		axios({
			method: 'post',
			url: `/email`,
			data: {
				author: admin,
				customer: id,
				subject,
			},
			...options(),
		})
			.then(() => {
				setSubject('');
				setWake(new Date().getTime());
			})
			.catch(console.log);
	};

	if (loading) return <p>Loading...</p>;

	return (
		<HStack align="top">
			<Box
				w="50%"
				borderRadius="md"
				border="1px"
				borderColor="gray.200"
				p={4}
				mr={4}
			>
				<Heading pb={6} fontSize={'xl'}>
					Management
				</Heading>
				<HStack pb={6}>
					<FormControl w="100%">
						<FormLabel>Critical Info</FormLabel>
						<Textarea
							placeholder="Enter some critical info here"
							value={newCritical}
							onChange={e => setNewCritical(e.target.value)}
							size="sm"
						/>
					</FormControl>
				</HStack>
				<HStack pb={6}>
					<FormControl w="50%">
						<ServiceSelect
							onChange={e => {
								setNewService(e.target.value);
								if (!twoStageServices.includes(e.target.value))
									setNewStage('1');
							}}
							{...{ newService, disabled }}
						/>
					</FormControl>
					<FormControl w="50%">
						{showStage && (
							<StageSelect
								onChange={e => setNewStage(e.target.value)}
								{...{ newStage, disabled }}
							/>
						)}
					</FormControl>
				</HStack>
				<HStack pb={3}>
					<StatusSelect
						onChange={e => setNewStatus(e.target.value)}
						{...{ newStatus, disabled }}
					/>
					<SecondarySelect
						onChange={e => setNewSecondary(e.target.value)}
						{...{ newSecondary }}
					/>
				</HStack>
				<HStack pb={3}>
					<FormControl>
						<FormLabel>Document Storage Link</FormLabel>
						<Input
							type="text"
							placeholder="https://drive.google.com/drive/u/0/folders/... some folder id ..."
							value={newStorageLink}
							onChange={e => setNewStorageLink(e.target.value)}
						/>
					</FormControl>
				</HStack>
				<HStack pb={6}>
					<FormControl w="50%">
						<FormLabel>Term</FormLabel>
						<Input
							type="text"
							placeholder="Customer interaction term"
							value={newTerm}
							onChange={e => setNewTerm(e.target.value)}
						/>
					</FormControl>
					<FormControl w="50%">
						<FormLabel>&nbsp;</FormLabel>
						<Button
							w="100%"
							colorScheme="blue"
							isDisabled={saveText === 'No changes'}
							onClick={handleSave}
						>
							{saveText}
						</Button>
					</FormControl>
				</HStack>
				<Divider mb={6} />
				{false && (
					<HStack pb={6}>
						<FormControl w="50%">
							<FormLabel>Subject</FormLabel>
							<Input
								type="text"
								placeholder="default: Contract"
								value={subject}
								onChange={e => setSubject(e.target.value)}
							/>
						</FormControl>
						<FormControl w="50%">
							<FormLabel>E-mail</FormLabel>
							<Tooltip label={user.email} placement="top-start">
								<Link
									href={`mailto:${user.email}`}
									_hover={{ textDecoration: 'none' }}
								>
									<Button
										d="block"
										w="100%"
										colorScheme="teal"
										onClick={handleEmail}
									>
										<EmailIcon />
										&nbsp; Send
									</Button>
								</Link>
							</Tooltip>
						</FormControl>
					</HStack>
				)}
				<Heading pb={4} fontSize={'lg'}>
					Tasks related to this customer
				</Heading>
				{tickets.map(ticket => (
					<Ticket
						key={ticket.id}
						{...{ ticket, workers, admin, setWakeTickets: setWake }}
					/>
				))}
				<NewTicket {...{ admin, customer: id, workers, setWake }} />
			</Box>
			<Box w="50%" borderRadius="md" border="1px" borderColor="gray.200" p={4}>
				<Heading pb={6} fontSize={'xl'}>
					Communications
				</Heading>
				<Stack spacing={3} maxH="70vh" overflowY="scroll">
					{comms.map(({ sender_id, customer_id, sent_at, sender, content }) =>
						sender_id === customer_id ? (
							<Flex>
								<Box borderRadius="md" bgColor="teal.100" maxW="70%" px={4}>
									<Box>{content}</Box>
									<Box fontSize="xs">
										{moment(sent_at).format('DD.MM.YYYY HH:mm:ss')}
									</Box>
								</Box>
								<Spacer />
								<Box>&nbsp;</Box>
							</Flex>
						) : (
							<Flex>
								<Box>&nbsp;</Box>
								<Spacer />
								<Box borderRadius="md" bgColor="blue.100" maxW="70%" px={4}>
									<Box fontSize="sm" fontWeight="700">
										{sender}
									</Box>
									<Box>{content}</Box>
									<Box fontSize="xs">
										{moment(sent_at).format('DD.MM.YYYY HH:mm:ss')}
									</Box>
								</Box>
							</Flex>
						)
					)}
				</Stack>
				<Divider my={6} />
				<HStack>
					<FormControl>
						<Textarea value={newComm} onChange={handleCommChange} size="sm" />
					</FormControl>
					<Button colorScheme="blue" onClick={handleSend}>
						Send
					</Button>
				</HStack>
			</Box>
		</HStack>
	);
};
